import React, {
  createRef,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import {
  useTranslate,
  isOutsideOpenEnrollment,
  getBeneFirstAndLastName,
  fetchOECStatus,
  useYearInTitle,
} from "../../helpers";
import {
  AppPage,
  BeneficiaryInfo,
  DegradedIntegrationAlert,
} from "../../components";
import { AppContext } from "../../app/store";
import { RouteComponentProps, Redirect } from "react-router";
import WhatMedicareCoverage from "../QuestionRoutingPage/WhatMedicareCoverage";
import { useFlags } from "launchdarkly-react-client-sdk";
import routes from "../../app/routes";
import { OecStatus } from "../../@types";

const BeneLandingPage: FunctionComponent<RouteComponentProps> = () => {
  const {
    state: { beneficiary, year },
  } = useContext(AppContext);
  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
    mctCurrentPlanYear,
  } = useFlags();
  const t = useTranslate();
  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });
  const forNextYear = year === (mctCurrentPlanYear + 1).toString();
  const beneInfoRef = createRef<HTMLUListElement>();
  const [pageHasLoaded, setPageHasLoaded] = useState(false);
  const [oecStatus, setOecStatus] = useState<OecStatus | undefined>();

  useEffect(() => setPageHasLoaded(true));

  useEffect(() => {
    (async () => {
      if (year) {
        const status = await fetchOECStatus(year);
        setOecStatus(status);
        if (pageHasLoaded) {
          // Give `BeneficiaryInfo `focus only upon changing year, not on page load
          beneInfoRef.current?.focus();
        }
      }
    })();
  }, [year]);

  // heading is the shown on the page, i.e. <h1>.
  const heading = t("bene_landing.h1");
  // title is the page title.
  const title = useYearInTitle(heading, year);

  return beneficiary ? (
    <AppPage heading={heading} title={title}>
      <div>
        <h2 className="mct-u-color--primary-light ds-u-font-weight--normal e2e-bene-landing-name">
          {getBeneFirstAndLastName(beneficiary, t)}
        </h2>
        <DegradedIntegrationAlert />
        <div
          className="ds-u-padding--2 ds-u-margin-bottom--4 ds-u-fill--gray-lightest"
          aria-live="polite"
        >
          {!outsideOpenEnrollment && (
            <div id="review_benefits">
              {forNextYear ? (
                <FormattedMessage
                  id="bene_landing.review_benefits"
                  values={{ year: mctCurrentPlanYear + 1 }}
                  tagName="p"
                />
              ) : (
                <p className="ds-u-visibility--screen-reader">
                  <FormattedMessage
                    id="bene_landing.review_benefits"
                    values={{ year: mctCurrentPlanYear }}
                    tagName="span"
                  />
                </p>
              )}
            </div>
          )}
          <BeneficiaryInfo
            bene={beneficiary}
            oecStatus={oecStatus}
            {...(!outsideOpenEnrollment
              ? { describedBy: "review_benefits" }
              : {})}
            ref={beneInfoRef}
          />
        </div>

        <WhatMedicareCoverage beneficiary={beneficiary} />
      </div>
    </AppPage>
  ) : (
    <Redirect to={routes.questionRouting} />
  );
};

export default BeneLandingPage;
